import React, { useState } from "react";
import { StyleSheet, View, ScrollView, TouchableOpacity } from "react-native";
import { router } from "expo-router";
import ThemedScreen from "@/components/ThemedScreen";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { ThemedPanel } from "@/components/ThemedPanel";
import { ComponentRefTag } from "@/components/design/ComponentRefTag";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useColorScheme } from "@/hooks/useColorScheme";
import { ThemedPlatformSymbol, PLATFORM_SYMBOLS, PlatformSymbolType } from "@/components/ThemedPlatformSymbol";
import { ThemedChip } from "@/components/ThemedChip";
import { ThemedCompactCard, ThemedFullCard, ThemedCustomCard } from "@/components/cards";
import { AutoSaveIndicator } from "@/components/ui/AutoSaveIndicator";

const ALL_SYMBOLS: PlatformSymbolType[] = [
  "owner",
  "fivestar",
  "saved",
  "shared",
  "heirloom",
  "readonly",
  "synced",
];

/**
 * Design Sandbox Workbench: Platform Symbols.
 * Showcases the canonical platform status indicators across sizes, labels, and card integrations.
 */
export default function SymbolsDesignScreen() {
  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const textMuted = useThemeColor({}, "textMuted");
  const tintColor = useThemeColor({}, "tint");
  const isDark = useColorScheme() === "dark";

  // AutoSave test status
  const [autoSaveStatus, setAutoSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("saved");

  return (
    <ThemedScreen
      title="Platform Symbols"
      leftIcon={{
        name: "arrow-back",
        onPress: () => router.navigate("/admin/design" as any),
      }}
    >
      <ScrollView contentContainerStyle={styles.container}>
        <ThemedText type="stamp" style={{ color: textMuted, marginBottom: 8 }}>
          CANONICAL STATUS SYMBOLS & SEMANTIC AFFORDANCES (UX STANDARD 15)
        </ThemedText>

        {/* 1. Master Symbols Grid */}
        <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
          <View style={styles.cardHeader}>
            <View style={{ flex: 1 }}>
              <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                Platform Symbols Registry
              </ThemedText>
              <ThemedText style={styles.sectionSubtitle}>
                Hover or tap any symbol to view its standardized tooltip. Strict semantic mapping prevents icon ambiguity.
              </ThemedText>
            </View>
            <ComponentRefTag name="REF: <ThemedPlatformSymbol>" />
          </View>

          <View style={styles.symbolsGrid}>
            {ALL_SYMBOLS.map((symKey) => {
              const cfg = PLATFORM_SYMBOLS[symKey];
              return (
                <View key={symKey} style={[styles.symbolCard, { borderColor }]}>
                  <View style={styles.symbolCardTop}>
                    <ThemedPlatformSymbol type={symKey} size="large" />
                    <ThemedText type="defaultSemiBold" style={{ fontSize: 13, textTransform: "capitalize" }}>
                      {symKey}
                    </ThemedText>
                  </View>
                  <ThemedText style={[styles.symbolMeta, { color: textMuted }]}>
                    Tooltip: "{cfg.tooltip}"
                  </ThemedText>
                  <ThemedText style={[styles.symbolMeta, { color: cfg.color, fontFamily: "SpaceMono" }]}>
                    {cfg.color}
                  </ThemedText>

                  {/* Size Matrix */}
                  <View style={styles.sizeRow}>
                    <View style={styles.sizeItem}>
                      <ThemedPlatformSymbol type={symKey} size="small" />
                      <ThemedText style={styles.sizeLabel}>sm (18px)</ThemedText>
                    </View>
                    <View style={styles.sizeItem}>
                      <ThemedPlatformSymbol type={symKey} size="medium" />
                      <ThemedText style={styles.sizeLabel}>md (22px)</ThemedText>
                    </View>
                    <View style={styles.sizeItem}>
                      <ThemedPlatformSymbol type={symKey} size="large" />
                      <ThemedText style={styles.sizeLabel}>lg (26px)</ThemedText>
                    </View>
                  </View>

                  {/* Pill with Label */}
                  <View style={{ marginTop: 8 }}>
                    <ThemedPlatformSymbol type={symKey} size="small" showLabel />
                  </View>
                </View>
              );
            })}
          </View>
        </ThemedView>

        {/* 2. AutoSave Indicator Integration */}
        <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
          <View style={styles.cardHeader}>
            <View style={{ flex: 1 }}>
              <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                AutoSave Indicator with "Saved" Cloud Symbol
              </ThemedText>
              <ThemedText style={styles.sectionSubtitle}>
                The "Saved" text label is replaced by the standardized Cloud with check symbol with a tooltip "Saved".
              </ThemedText>
            </View>
            <ComponentRefTag name="REF: <AutoSaveIndicator>" />
          </View>

          <View style={styles.autoSavePreviewRow}>
            <View style={styles.autoSaveButtons}>
              <TouchableOpacity
                style={[styles.testButton, autoSaveStatus === "saving" && { borderColor: tintColor, borderWidth: 1 }]}
                onPress={() => setAutoSaveStatus("saving")}
              >
                <ThemedText style={styles.testBtnText}>Simulate Saving</ThemedText>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.testButton, autoSaveStatus === "saved" && { borderColor: tintColor, borderWidth: 1 }]}
                onPress={() => setAutoSaveStatus("saved")}
              >
                <ThemedText style={styles.testBtnText}>Simulate Saved</ThemedText>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.testButton, autoSaveStatus === "error" && { borderColor: tintColor, borderWidth: 1 }]}
                onPress={() => setAutoSaveStatus("error")}
              >
                <ThemedText style={styles.testBtnText}>Simulate Error</ThemedText>
              </TouchableOpacity>
            </View>

            <View style={styles.indicatorContainer}>
              <AutoSaveIndicator status={autoSaveStatus} />
            </View>
          </View>
        </ThemedView>

        {/* 3. Card Integration Showcases */}
        <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
          <View style={styles.cardHeader}>
            <View style={{ flex: 1 }}>
              <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                Item Card Integration (Tier 1 to Tier 4)
              </ThemedText>
              <ThemedText style={styles.sectionSubtitle}>
                Platform symbols embedded into Chips, Compact, Full, and Custom cards.
              </ThemedText>
            </View>
            <ComponentRefTag name="REF: <ThemedChip> / <ThemedCompactCard>" />
          </View>

          {/* Tier 1 Chips with Owner & 5-Star */}
          <ThemedText type="defaultSemiBold" style={styles.subTitle}>
            Tier 1: ThemedChip with Platform Symbols
          </ThemedText>
          <View style={styles.chipsRow}>
            <ThemedChip
              entityType="memory"
              label="1974 Big Sur Route"
              isOwner={true}
              isFiveStar={true}
            />
            <ThemedChip
              entityType="contact"
              label="Evelyn Vance"
              isOwner={true}
            />
            <ThemedChip
              entityType="group"
              label="Historical Oral Archives"
              isFiveStar={true}
            />
            <ThemedChip
              entityType="media"
              label="Pacific Sunset Slide"
              thumbnailUrl="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=100&auto=format&fit=crop&q=60"
              isOwner={true}
              isFiveStar={true}
            />
          </View>

          {/* Tier 2 Compact Cards */}
          <ThemedText type="defaultSemiBold" style={[styles.subTitle, { marginTop: 16 }]}>
            Tier 2: ThemedCompactCard with Platform Symbols
          </ThemedText>
          <View style={{ gap: 8 }}>
            <ThemedCompactCard
              entityType="memory"
              id="mem-1"
              title="1974 Big Sur Pacific Expedition"
              secondaryText="Aug 15, 1974 • 14 slides"
              isOwner={true}
              isFiveStar={true}
            />
            <ThemedCompactCard
              entityType="contact"
              id="con-1"
              title="Dr. Arthur Pendelton"
              secondaryText="arthur.pendelton@archival.org • Collaborator"
              isOwner={true}
            />
          </View>

          {/* Tier 3 Full Card with Symbol Tray */}
          <ThemedText type="defaultSemiBold" style={[styles.subTitle, { marginTop: 16 }]}>
            Tier 3: ThemedFullCard with Labeled Platform Symbol Tray
          </ThemedText>
          <ThemedFullCard
            entityType="memory"
            id="mem-full-1"
            title="Cedar Chest Handwritten Letters (1942)"
            displayDate="Sep 12, 1942"
            ownerDisplayName="Eleanor Vance"
            description="Handwritten correspondence between coastal archivists during WWII mobilization."
            isOwner={true}
            isFiveStar={true}
          />
        </ThemedView>

        {/* 4. Specifications & Rules */}
        <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
          <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
            Platform Symbols Architectural Standard
          </ThemedText>
          <ThemedText style={styles.ruleItem}>
            1. <ThemedText type="defaultSemiBold">Owner (Gold Key):</ThemedText> Used whenever the authenticated user is the canonical author/creator/owner of the item. Icon: <ThemedText type="code">key</ThemedText>, Color: <ThemedText type="code">#D4AF37</ThemedText>, Tooltip: <ThemedText type="code">"Owner"</ThemedText>.
          </ThemedText>
          <ThemedText style={styles.ruleItem}>
            2. <ThemedText type="defaultSemiBold">5-Star Rating (Gold Star):</ThemedText> Used for items marked with favourite rating of 5. Icon: <ThemedText type="code">star</ThemedText>, Color: <ThemedText type="code">#EAB308</ThemedText>, Tooltip: <ThemedText type="code">"5 stars"</ThemedText>.
          </ThemedText>
          <ThemedText style={styles.ruleItem}>
            3. <ThemedText type="defaultSemiBold">Saved (Cloud with Check):</ThemedText> Replaces "Saved" text in AutoSaveIndicator and status panels. Icon: <ThemedText type="code">cloud-done</ThemedText>, Color: <ThemedText type="code">#10B981</ThemedText>, Tooltip: <ThemedText type="code">"Saved"</ThemedText>.
          </ThemedText>
          <ThemedText style={styles.ruleItem}>
            4. <ThemedText type="defaultSemiBold">Shared with You:</ThemedText> Inbound items shared by contacts. Icon: <ThemedText type="code">people</ThemedText>, Color: <ThemedText type="code">#0284C7</ThemedText>, Tooltip: <ThemedText type="code">"Shared with you"</ThemedText>.
          </ThemedText>
          <ThemedText style={styles.ruleItem}>
            5. <ThemedText type="defaultSemiBold">Physical Heirloom:</ThemedText> Tangible physical artifacts. Icon: <ThemedText type="code">archive</ThemedText>, Color: <ThemedText type="code">#B45309</ThemedText>, Tooltip: <ThemedText type="code">"Physical Heirloom"</ThemedText>.
          </ThemedText>
          <ThemedText style={styles.ruleItem}>
            6. <ThemedText type="defaultSemiBold">Read-Only:</ThemedText> Non-editable view items. Icon: <ThemedText type="code">lock-closed</ThemedText>, Color: <ThemedText type="code">#64748B</ThemedText>, Tooltip: <ThemedText type="code">"Read-only"</ThemedText>.
          </ThemedText>
          <ThemedText style={styles.ruleItem}>
            7. <ThemedText type="defaultSemiBold">Available Offline:</ThemedText> Locally cached / synchronized items. Icon: <ThemedText type="code">cloud-download</ThemedText>, Color: <ThemedText type="code">#0EA5E9</ThemedText>, Tooltip: <ThemedText type="code">"Available Offline"</ThemedText>.
          </ThemedText>
        </ThemedView>
      </ScrollView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
    gap: 16,
  },
  card: {
    borderWidth: 1,
    padding: 16,
    borderRadius: 4,
    gap: 12,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
  },
  sectionTitle: {
    fontSize: 15,
  },
  sectionSubtitle: {
    fontSize: 12,
    opacity: 0.75,
    marginTop: 2,
  },
  subTitle: {
    fontSize: 13,
    marginTop: 8,
    marginBottom: 4,
  },
  symbolsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 8,
  },
  symbolCard: {
    flex: 1,
    minWidth: 160,
    borderWidth: 1,
    padding: 12,
    borderRadius: 3,
    backgroundColor: "rgba(150, 150, 150, 0.04)",
  },
  symbolCardTop: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 6,
  },
  symbolMeta: {
    fontSize: 11,
    marginTop: 2,
  },
  sizeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginTop: 10,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "rgba(150, 150, 150, 0.15)",
  },
  sizeItem: {
    alignItems: "center",
    gap: 3,
  },
  sizeLabel: {
    fontSize: 9,
    opacity: 0.6,
  },
  autoSavePreviewRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  autoSaveButtons: {
    flexDirection: "row",
    gap: 8,
  },
  testButton: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: "rgba(150, 150, 150, 0.3)",
    backgroundColor: "rgba(150, 150, 150, 0.08)",
  },
  testBtnText: {
    fontSize: 11,
    fontWeight: "600",
  },
  indicatorContainer: {
    padding: 4,
  },
  chipsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  ruleItem: {
    fontSize: 12,
    lineHeight: 18,
    marginTop: 4,
  },
});
